import { environment } from '../environments/environment';
import {
  APPLICATION_NAMES,
  ENCOMPASS_URL,
  EPM_URL,
  PeConnAccessToken,
  REFRESHER,
} from '@share/constants';
import { RefresherStatus } from './data-services/refresher-status.enum';
import { Router, RoutesRecognized } from '@angular/router';
import { AuthenticationManager } from './core/oauth/authentication-manager.service';
import { Subscription } from 'rxjs';
import { Component, NgZone, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'conn-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  readonly APPLICATION_NAMES = APPLICATION_NAMES;
  logoutText =
    'You have been logged out. You can close this tab or click the button below to login again.';
  hideLoginButton = false;
  version = environment?.version;
  title = 'Patient Engagement Management';
  isLoading: boolean;
  broadcastChannel: BroadcastChannel;
  subscriptionRefresh: Subscription;
  logoutUrl: SafeResourceUrl;
  accessToken: PeConnAccessToken;
  isLoggingOut = false;
  isOnErrorPage = false;
  isEyefinityApp = false;
  showPanel = false;
  routedFromEpm: boolean;
  isUserLoggedIn: boolean;
  hideFooter: boolean;

  constructor(
    private router: Router,
    private authenticationManager: AuthenticationManager,
    private sanitizer: DomSanitizer,
    private logger: NGXLogger,
    private ngZone: NgZone,
  ) {
    this.isLoading = true;
    this.broadcastChannel = new BroadcastChannel(REFRESHER);
    this.subscriptionRefresh = null;
  }

  ngOnInit() {
    this.isUserLoggedIn = false;
    this.loadDynamicCss();
    this.logoutUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.logoutUrl,
    );
    this.authenticationManager.getSwitchedToken().subscribe((newToken) => {
      if (!!newToken) {
        this.accessToken = newToken;
      }
    });
    this.accessToken = this.authenticationManager.getAccessToken();

    // removing login button for encompass users and changing logout text accordingly
    if (this.accessToken.application === APPLICATION_NAMES.encompass) {
      this.logoutText = 'You have been logged out. You can close this tab.';
      this.hideLoginButton = true;
    }

    this.isEyefinityApp =
      this.accessToken.application === APPLICATION_NAMES.eyefinity;
    // adding logic to handle logout in eyefinity.com
    if (this.isEyefinityApp) {
      // Optionally, implement an origin check here using e.origin.
      // Compare it against a list of trusted origins (e.g., eyefinity.com domains)
      // which should be stored securely in the environment configuration.
      window.addEventListener('message', (e) => {
        if (e?.data.type === 'logout') {
          this.authenticationManager.logout();
        }
      });
    }
    if (this.isEyefinityApp && this.accessToken.signupStatusEwx !== 'Manage') {
      this.hideFooter = true;
    }
    this.broadcastChannel.onmessage = (e) => {
      if (e?.data) {
        this.logger.info(
          'Received message from broadcast channel in app component on app state: ' +
            e.data +
            ' and this is the user state:' +
            this.isUserLoggedIn,
        );
        if (e.data === RefresherStatus.loading) {
          this.ngZone.run(() => {
            this.isLoading = true;
          });
        }
        if (e.data === RefresherStatus.done) {
          this.ngZone.run(() => {
            this.isLoading = false;
            this.isUserLoggedIn = true;
          });
        }
      }
    };
    this.router.events.subscribe((val) => {
      this.routedFromEpm = this.isRoutingFromEpm();
      if (val instanceof RoutesRecognized) {
        if (!this.subscriptionRefresh) {
          this.subscriptionRefresh = this.authenticationManager
            .refreshAuthToken(val.state.root)
            .subscribe(
              (result) => {
                this.logger.debug('Was refresh successful?: ', result);
                this.isOnErrorPage = !result;
              },
              (err) => {
                this.isOnErrorPage = true;
                this.logger.error('Refresh encountered an error: ', err);
              },
            );
        }
      }
    });
  }

  loadDynamicCss() {
    if (window.self !== window.top) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'http://localhost:8087/secure-eweb/common-web-tier/main.css';
      document.head.appendChild(link);
    }
  }

  /**
   * Displays the logout screen and loads an iframe to invalidate the PingFederate session.
   */
  logout() {
    this.isLoggingOut = true;
  }

  /**
   * Called when the logout iframe loads, indicating a successful logout in PingFederate.
   * At this point, pe-conn can safely clear the session storage.
   */
  performLogout() {
    this.authenticationManager.logout();
  }

  /**
   * Attempts login through authenticationManager
   */
  login() {
    this.authenticationManager.login();
  }

  /**
   * Checks whether the user's route contains an EPM URL in order to display the EPM logo in the header.
   * Users from Encompass should also be considered for displaying the EPM logo.
   */
  isRoutingFromEpm(): boolean {
    if (this.router && this.router.url) {
      return (
        this.router.url.toString().includes(EPM_URL) ||
        this.router.url.toString().includes(ENCOMPASS_URL)
      );
    }
    return false;
  }
}
